/**
 * Personal reminders API (Remindere).
 *
 * Reminders are strictly private: the server scopes every route to the caller,
 * so this client never sends or receives another user's rows. One list feeds
 * both the Reminders page and the global red pre-due banner.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface Reminder {
  id: string;
  title: string;
  description: string | null;
  /** ISO due moment; the banner fires 10 minutes before it. */
  dueAt: string;
  /** Optional duration estimate in minutes; sizes the calendar block. */
  estimatedMinutes: number | null;
  done: boolean;
  doneAt: string | null;
  /** ISO when the banner was dismissed for the current firing, null = armed. */
  ackAt: string | null;
  createdAt: string;
}

export interface RemindersResult {
  reminders: Reminder[];
  /** Server clock at response time, used to correct the client's own drift. */
  serverNow: string;
}

export interface ReminderPatch {
  title?: string;
  description?: string | null;
  /** ISO. Any change clears ackAt server-side, re-arming the banner. */
  dueAt?: string;
  estimatedMinutes?: number | null;
  done?: boolean;
  /** true = dismiss the banner for the current firing. */
  ack?: boolean;
}

export async function listReminders(): Promise<RemindersResult> {
  const res = await apiFetch<Envelope<RemindersResult>>('/api/reminders', {
    headers: authHeaders(),
  });
  // Test Mode's generic fallback has no `reminders` key — read defensively so a
  // missing mock route cannot blank every page.
  return {
    reminders: res.data?.reminders ?? [],
    serverNow: res.data?.serverNow ?? new Date().toISOString(),
  };
}

export async function createReminder(input: {
  title: string;
  description?: string | null;
  dueAt: string;
  estimatedMinutes?: number | null;
}): Promise<Reminder> {
  const res = await apiFetch<Envelope<{ reminder: Reminder }>>('/api/reminders', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.reminder;
}

export async function updateReminder(id: string, patch: ReminderPatch): Promise<Reminder> {
  const res = await apiFetch<Envelope<{ reminder: Reminder }>>(`/api/reminders/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
  return res.data.reminder;
}

export async function deleteReminder(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(`/api/reminders/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}
